import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import Login from './pages/login/Login';
import ForgotPassword from './pages/login/ForgotPassword';
import ResetPassword from './pages/login/ResetPassword';
import Dashboard from './pages/dashboard/Dashboard';
import UserManagement from './pages/users/UserManagement';
import UserProfile from './pages/users/UserProfile';
import AdminSettings from './pages/admin/AdminSettings';
import WarehouseManagement from './pages/warehouses/WarehouseManagement';
import StockManagement from './pages/stocks/StockManagement';
import SupplierManagement from './pages/suppliers/SupplierManagement';
import SupplierDetail from './pages/suppliers/SupplierDetail';
import Inventory from './pages/inventory/Inventory';
import PrintLabels from './pages/inventory/PrintLabels';
import StockTransfer from './pages/stocks/StockTransfer';
import StockScanner from './pages/stocks/StockScanner';
import Analytics from './pages/analytics/Analytics';
import SmartOrder from './pages/stocks/SmartOrder';
import PurchaseOrderList from './pages/purchase-orders/PurchaseOrderList';
import CompanySettings from './pages/admin/CompanySettings';

import MobileLayout from './pages/mobile/MobileLayout';
import MobileDashboard from './pages/mobile/MobileDashboard';
import MobileScanner from './pages/mobile/MobileScanner';
import MobileOrderReception from './pages/mobile/MobileOrderReception';
import MobileStockTransfer from './pages/mobile/MobileStockTransfer';
import MobileSettings from './pages/mobile/MobileSettings';
import MobileOrderSearch from './pages/mobile/MobileOrderSearch';
import MobileWarehouse from './pages/mobile/MobileWarehouse';

import HelpSupport from './pages/help/HelpSupport';
import MainLayout from './components/layout/MainLayout';
import { UIProvider } from './context/UIContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { SocketProvider } from './context/SocketContext';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, hasRole } = useAuth();

  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !hasRole(allowedRoles)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const HelpSupportWrapper = () => {
  const { user } = useAuth();

  if (user) {
    return (
      <MainLayout>
        <HelpSupport />
      </MainLayout>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col overflow-y-auto bg-background-light dark:bg-background-dark text-slate-900 dark:text-slate-100 font-display transition-colors px-4 pb-6 pt-24 sm:pt-14 sm:px-10">
      <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-50">
        <Link to="/login" className="flex items-center gap-2 text-primary hover:text-blue-500 font-bold bg-white dark:bg-slate-800 px-4 py-2 rounded-xl shadow-md border border-slate-200 dark:border-slate-700 transition-all hover:-translate-y-0.5">
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          Retour connexion
        </Link>
      </div>
      <div className="max-w-[1600px] mx-auto w-full flex flex-col gap-8">
        <HelpSupport />
      </div>
    </div>
  );
};

function App() {
  // Redirection automatique vers mobile si nécessaire
  useEffect(() => {
    const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const path = window.location.pathname;

    if (isMobile && (path === '/' || path === '/dashboard' || path === '')) {
      window.location.href = '/mobile';
    }
  }, []);

  return (
    <AuthProvider>
      <SettingsProvider>
        <UIProvider>
          <SocketProvider>
            <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Dashboard />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/stocks"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <StockManagement />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/stock-scanner"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <StockScanner />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/stock-transfer"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <StockTransfer />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />


                <Route
                  path="/inventory"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Inventory />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/inventory/print-labels"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <MainLayout>
                        <PrintLabels />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/suppliers"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <SupplierManagement />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/suppliers/:id"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <SupplierDetail />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Analytics />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/help"
                  element={<HelpSupportWrapper />}
                />
                <Route
                  path="/smart-order"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <SmartOrder />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/purchase-orders"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <PurchaseOrderList />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />


                <Route
                  path="/warehouses"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <WarehouseManagement />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <UserProfile />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/users"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <MainLayout>
                        <UserManagement />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/settings"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <MainLayout>
                        <AdminSettings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/company"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <MainLayout>
                        <CompanySettings />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Mobile Routes */}
                <Route
                  path="/mobile"
                  element={
                    <ProtectedRoute>
                      <MobileLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<MobileDashboard />} />
                  <Route path="scanner" element={<MobileScanner />} />
                  <Route path="orders" element={<MobileOrderSearch />} />
                  <Route path="orders/:id" element={<MobileOrderReception />} />
                  <Route path="transfer" element={<MobileStockTransfer />} />
                  <Route path="warehouses" element={<MobileWarehouse />} />
                  <Route path="settings" element={<MobileSettings />} />
                </Route>

                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Router>
          </SocketProvider>
        </UIProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
